import {
  ScheduleConfigContent,
  ScheduleConfigFormSectionWrapper,
  ScheduleTitle,
  ScheduleConfigLabel,
  WeekDayOption,
  DateSelectWrapper,
  DateSelectLabel,
  Placeholder,
  CheckboxLabel,
  StyledSwitch,
} from "./styled"
import { FlexBox } from "components/Common/FlexBox"
import Dropdown from "components/inputs/Dropdown"
import { Action } from "antd-mobile/es/components/action-sheet"

import { capitalizeFirstLetter } from "utils/formater"
import { Fragment, useState } from "react"
import CalendarIcon from "components/Icons/CalendarIcon"
import { StyledPopup } from "components/Common/StyledPopup"
import dayjs, { Dayjs } from "dayjs"
import RangePicker from "components/pickers/RangePicker"
import { DAYS_OF_WEEK } from "config"
import Checkbox from "components/inputs/Checkbox"
import { IScheduleConfig, ScheduleDayOfWeekType, ScheduleType } from "model/food/types/schedule.types"

interface IScheduleConfigFormSectionProps {
  scheduleConfig: IScheduleConfig
  onScheduleTypeChange: (value: string) => void
  onSchedulePeriodChange: (period: { start: Dayjs; end?: Dayjs }) => void
  onDayOfWeekChange: (day: string) => void
  isNoEndDate: boolean
  onNoEndDateChange: (checked: boolean) => void
  hasSchedule: boolean
  setHasSchedule: (value: boolean) => void
  isUpdateMode?: boolean
}

const ScheduleConfigFormSection = (props: IScheduleConfigFormSectionProps) => {
  const {
    scheduleConfig,
    onDayOfWeekChange,
    onSchedulePeriodChange,
    onScheduleTypeChange,
    isNoEndDate,
    onNoEndDateChange,
    hasSchedule,
    setHasSchedule,
    isUpdateMode,
  } = props
  const [showRangePicker, setShowRangePicker] = useState(false)

  const { startDate, endDate, daysOfWeek } = scheduleConfig

  const start = startDate ? dayjs(startDate) : undefined
  const end = endDate ? dayjs(endDate) : undefined

  const scheduleTypeOptions: Action[] = [
    {
      key: "DAILY",
      text: "Daily",
    },
    {
      key: "WEEKLY",
      text: "Weekly",
    },
    {
      key: "CUSTOM",
      text: "Custom",
    },
  ]

  const displayedScheduleType = capitalizeFirstLetter(
    scheduleConfig.type.toLowerCase(),
  )

  const disableDayBeforeToday = (date: Dayjs) => {
    const isEarlyThanToday = date.isBefore(dayjs().startOf("day"), "day")

    return isEarlyThanToday
  }

  return (
    <ScheduleConfigFormSectionWrapper>
      <FlexBox $justify="space-between" $alignItems="center">
        <ScheduleTitle>Repeat this Meal</ScheduleTitle>
        {!isUpdateMode && (
          <StyledSwitch
            checked={hasSchedule}
            onChange={(checked) => {
              setHasSchedule(checked)
            }}
            style={{
              "--width": "40px",
              "--height": "24px",
              "--checked-color": "#4BD9DE",
            }}
          />
        )}
      </FlexBox>

      <ScheduleConfigContent
        $isDisabled={!hasSchedule}
        style={{ pointerEvents: hasSchedule ? "auto" : "none" }}
      >
        <FlexBox $gap={8} $alignItems="center">
          <ScheduleConfigLabel>Repeat:</ScheduleConfigLabel>
          <Dropdown
            label={displayedScheduleType}
            value={scheduleConfig.type}
            options={scheduleTypeOptions}
            onChange={(val) => onScheduleTypeChange(val as ScheduleType)}
          />
        </FlexBox>

        {scheduleConfig.type !== "DAILY" && (
          <FlexBox $gap={12} $alignItems="center">
            <ScheduleConfigLabel>On:</ScheduleConfigLabel>

            <FlexBox $gap={8} $alignItems="center">
              {DAYS_OF_WEEK.map((day, index) => (
                <WeekDayOption
                  onClick={() => onDayOfWeekChange(day)}
                  key={index}
                  $isSelected={daysOfWeek.includes(day)}
                >
                  {day[0]}
                </WeekDayOption>
              ))}
            </FlexBox>
          </FlexBox>
        )}

        {scheduleConfig.type === "CUSTOM" && (
          <FlexBox $gap={12} $alignItems="center">
            <ScheduleConfigLabel style={{ minWidth: 37 }}>
              Period:
            </ScheduleConfigLabel>
            <StyledPopup
              position="top"
              visible={showRangePicker}
              onMaskClick={() => setShowRangePicker(false)}
            >
              <RangePicker
                isDisabledDate={(date) => {
                  return disableDayBeforeToday(date)
                }}
                isSingleMode={isNoEndDate}
                onChange={(value) => {
                  onSchedulePeriodChange(value)
                  setShowRangePicker(false)
                }}
                start={start}
                end={end}
                isHighlightDate={(date) => {
                  return daysOfWeek.includes(
                    date.format("dddd").toUpperCase() as ScheduleDayOfWeekType,
                  )
                }}
              />
            </StyledPopup>

            <DateSelectWrapper
              $isActive={showRangePicker}
              onClick={() => {
                if (hasSchedule) {
                  setShowRangePicker(true)
                }
              }}
            >
              <DateSelectLabel>
                {start ? (
                  start.format("MMM DD")
                ) : (
                  <Placeholder>Start</Placeholder>
                )}
              </DateSelectLabel>

              {!isNoEndDate && (
                <Fragment>
                  {" - "}
                  <DateSelectLabel>
                    {end ? (
                      end.format("MMM DD")
                    ) : (
                      <Placeholder>End</Placeholder>
                    )}
                  </DateSelectLabel>
                </Fragment>
              )}

              <FlexBox
                $alignItems="center"
                style={{ marginLeft: isNoEndDate ? "auto" : "4px" }}
              >
                <CalendarIcon />
              </FlexBox>
            </DateSelectWrapper>

            <FlexBox
              style={{ marginLeft: "auto" }}
              $alignItems="center"
              $gap={4}
              onClick={() => onNoEndDateChange(!isNoEndDate)}
            >
              <Checkbox uncheckIconFill="#69757D" checked={isNoEndDate} />
              <CheckboxLabel>Never ends</CheckboxLabel>
            </FlexBox>
          </FlexBox>
        )}
      </ScheduleConfigContent>
    </ScheduleConfigFormSectionWrapper>
  )
}

export default ScheduleConfigFormSection
